import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, X, Share2 } from 'lucide-react';

const LS_DISMISS_KEY = 'kioo_pwa_install_dismissed_at';
// Ask again 7 days after the user dismisses the banner.
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const wasRecentlyDismissed = () => {
  try {
    const ts = parseInt(localStorage.getItem(LS_DISMISS_KEY) || '0', 10);
    return ts && Date.now() - ts < DISMISS_MS;
  } catch (_e) {
    return false;
  }
};

/**
 * Floating "Install App" prompt for the mobile PWA.
 * - Chrome/Android: captures the `beforeinstallprompt` event and shows a
 *   one-tap install button that calls `prompt()`.
 * - iOS Safari: shows an "Add to Home Screen" hint (Share icon → Add).
 * - Auto-hides when the app is already installed or when dismissed recently.
 */
const InstallAppButton = () => {
  const deferredPromptRef = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const alreadyInstalled = useMemo(isStandalone, []);
  const iosDevice = useMemo(isIOS, []);

  useEffect(() => {
    if (alreadyInstalled) return;
    if (wasRecentlyDismissed()) {
      setDismissed(true);
      return;
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };
    const onInstalled = () => {
      setCanInstall(false);
      deferredPromptRef.current = null;
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    // iOS Safari never fires beforeinstallprompt — schedule the manual hint
    // after a short delay so it doesn't fight with the initial page render.
    let iosTimer = null;
    if (iosDevice) {
      iosTimer = setTimeout(() => setShowIOSHint(true), 3500);
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [alreadyInstalled, iosDevice]);

  const handleInstall = async () => {
    const dp = deferredPromptRef.current;
    if (!dp) return;
    try {
      dp.prompt();
      const choice = await dp.userChoice;
      if (choice?.outcome === 'accepted') {
        setCanInstall(false);
      }
    } catch (_e) {
      /* user cancelled */
    } finally {
      deferredPromptRef.current = null;
      setCanInstall(false);
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(LS_DISMISS_KEY, String(Date.now()));
    } catch (_e) {
      /* ignore */
    }
    setDismissed(true);
    setCanInstall(false);
    setShowIOSHint(false);
  };

  if (alreadyInstalled || dismissed) return null;
  if (!canInstall && !showIOSHint) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed left-3 right-3 bottom-[76px] z-40 rounded-2xl shadow-2xl border border-black/5 text-white px-4 py-3 flex items-center gap-3"
      style={{
        background:
          'linear-gradient(135deg, var(--kioo-primary-dark) 0%, var(--kioo-primary) 100%)',
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: 'var(--kioo-accent)' }}
      >
        <Download className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold leading-tight">
          Install Kioo Radio
        </div>
        {canInstall ? (
          <div className="text-[11px] opacity-90 leading-tight">
            Add it to your home screen for one-tap listening.
          </div>
        ) : (
          <div className="text-[11px] opacity-90 leading-tight inline-flex items-center gap-1">
            Tap
            <Share2 className="w-3 h-3" />
            then <b>Add to Home Screen</b>.
          </div>
        )}
      </div>
      {canInstall && (
        <button
          data-testid="pwa-install-btn"
          onClick={handleInstall}
          className="px-3 py-2 rounded-full bg-white text-emerald-800 text-xs font-extrabold shadow ring-1 ring-black/10 active:scale-95 transition-transform"
        >
          Install
        </button>
      )}
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default InstallAppButton;
